// Internal modules
import { features, getContent } from '@/lib';
import type { DefaultInputState } from '@/store';
import { appStore } from '@/store';
import { stripAddressPrefix } from './miscUtils';
import { isConversionNeeded } from '@/lib/utils/currencyConversion';
import { isConversionChangeable } from '@/components/PaymentInformation/CardFields/CurrencyConversion';
// Untyped imports
import {
  hasOwnProp,
  isObject, // @ts-expect-error react-components
} from '@checkout/react-components';

// Types
import type {
  FormAddressState,
  FormAddressTypes,
  FormState,
} from '@/store/models/form/types';
import type { AddressType, ValueOf } from '@/types';

/* === Begin Types specific to this file === */
export type FormEventTarget = {
  id: string;
  name: keyof FormState;
  type: string;
};

type AddressParams = {
  addressType?: AddressType;
};
type GetFieldCurrentStateParams = {
  field: keyof FormState;
  addressType?: AddressType;
};

type GetFieldDataReturnType = {
  addressType?: FormAddressTypes;
  currentState: ValueOf<FormState>;
  field: keyof FormState;
};

type BankState = Partial<
  Pick<
    FormState,
    | 'bankAccountType'
    | 'bankAccountNumber'
    | 'bankSortCode'
    | 'bankIban'
    | 'bankSortCode'
  >
>;

type CardState = Partial<
  Pick<FormState, 'cardCvv' | 'cardExpiry' | 'cardNumber'> &
    Partial<Pick<FormState, 'currencyConversionSelection' | 'cardProductClass'>>
>;

type Installment = {
  installmentTerm?: DefaultInputState<string>;
};

type FormTab = BankTab | PayNowTab | PayLaterTab;

type BankTab = {
  fiType: 'bank';
  form: () => JSX.Element;
  fptiName: 'pay_now_bank';
  inactive: boolean;
  tabId: 'bank-tab';
};
type PayNowTab = {
  fiType: 'card';
  form: () => JSX.Element;
  fptiName: 'pay_now_card';
  tabId: 'card-tab';
};
type PayLaterTab = {
  fiType: 'bnpl';
  form: () => JSX.Element;
  fptiName: 'pay_later';
  inactive: boolean;
  tabId: 'bnpl-tab';
};

/* === End Types specific to this file === */

/**
 * @function getFieldData
 * @param {object} target | event.target object
 * @param {object} meta | metadata object passed to change handler
 * @returns {object}
 */
const getFieldData = (
  { id, name, type }: React.FocusEvent<HTMLInputElement>['target'],
  { addressType }: AddressParams,
): GetFieldDataReturnType => {
  const isRadio = type === 'radio';
  const field = (isRadio ? name : stripAddressPrefix(id)) as keyof FormState;
  const currentState = getFieldCurrentState({ addressType, field });

  return {
    addressType: addressType && `${addressType}Address`,
    currentState,
    field,
  };
};

/**
 * @function getFieldCurrentState
 * @param {object} params | An object
 * @param {string} params.field | field from formState
 * @param {string} params.addressType | billing or shipping address
 * @returns {object}
 */
const getFieldCurrentState = ({
  field,
  addressType,
}: GetFieldCurrentStateParams): ValueOf<FormState> => {
  const { form: formState } = appStore.getState();

  if (!addressType) {
    return formState[field];
  }

  // Type cast to Form Address Type
  const addressField = `${addressType}Address` as FormAddressTypes;
  return formState[addressField];
};

/**
 * @function getForcedValidCopy
 * @param {object} fieldGroup
 * @returns {object}
 * @description Creates a copy of a fieldGroup (e.g. billingAddress)
 *  and sets all `isValid` keys to `true`. This is needed for when
 *  the user can't edit the address fields to fix any validation errors.
 */

const getForcedValidCopy = <
  T extends
    | FormAddressState['billingAddress']
    | FormAddressState['shippingAddress'],
>(
  fieldGroup: T,
): T => {
  const entries = Object.entries(fieldGroup || {}) as [keyof T, ValueOf<T>][];
  const copy = entries.reduce((accum, [key, data]) => {
    if (!isObject(data)) {
      accum[key] = data;
      return accum;
    }

    accum[key] = {
      ...data,
      ...(hasOwnProp(data, 'isValid') && { isValid: true }),
    };
    return accum;
  }, {} as T);

  return copy;
};

/**
 * @function getCommonFormState
 * @param {object} formState
 * @returns {object}
 * @description Takes formState and returns an object containing all the
 *  values that are common to both guests and signup.
 */
const getCommonFormState = (formState: FormState): Partial<FormState> => {
  const {
    dialingCode,
    email,
    firstName,
    lastName,
    onboardOption,
    phone,
    phoneType,
  } = formState;

  return {
    dialingCode,
    email,
    firstName,
    lastName,
    onboardOption,
    phone,
    phoneType,
  };
};

/**
 * @function getBankState
 * @param {object} formState
 * @returns {object}
 * @description Takes formState and returns an object containing
 * bank fields.
 */

const getBankState = (formState: FormState): BankState => {
  const { ui: uiState } = appStore.getState();
  const { fiType } = uiState;
  const { bankAccountNumber, bankAccountType, bankIban, bankSortCode } =
    formState;

  if (fiType !== 'bank') {
    return {};
  }

  const bankState: BankState = {};
  if (bankAccountType.value === 'bank') {
    bankState.bankAccountNumber = bankAccountNumber;
    bankState.bankSortCode = bankSortCode;
  } else {
    bankState.bankIban = bankIban;
  }

  return {
    bankAccountType,
    ...bankState,
  };
};

const getDialingCode = (data: FormState): string | number | undefined => {
  const {
    api: { griffinMetadata },
  } = appStore.getState();
  const dialingCodeMap = griffinMetadata.territoriesToDialingCodeMap;
  return dialingCodeMap[data?.dialingCode?.value];
};

/**
 * @function getCardState
 * @param {object} formState
 * @returns {object}
 * @description Takes formState and returns an object containing
 * card fields.
 */
const getCardState = (formState: FormState): CardState => {
  const { ui: uiState } = appStore.getState();
  const { fiType } = uiState;

  if (fiType !== 'card') {
    return {};
  }

  // Only include conversion selection data if it is needed
  const withConversion = isConversionNeeded() && isConversionChangeable();
  const {
    cardCvv,
    cardExpiry,
    cardNumber,
    currencyConversionSelection,
    cardProductClass,
  } = formState;

  return {
    cardCvv,
    cardExpiry,
    cardNumber,
    ...(features.isActive('shouldCollectCardProductClass') && {
      cardProductClass,
    }),
    ...(withConversion && { currencyConversionSelection }),
  };
};

/**
 * @function getInstallmentTermState
 * @param {object} formState
 * @returns {object}
 * @description Takes formState and returns an object containing
 * installment term.
 */
const getInstallmentTermState = (formState: FormState): Installment => {
  const { ui: uiState } = appStore.getState();
  const { isInstallmentButtonShown } = uiState;
  const { installmentTerm } = formState;

  if (!isInstallmentButtonShown) {
    return {};
  }

  return {
    installmentTerm,
  };
};

const getFormTabContent = (
  tab: FormTab['fiType'],
): { title: React.ReactNode[] | string } => {
  if (tab === 'bank') {
    const bankTitle = getContent('addBank.bankTabLabel');

    return { title: bankTitle };
  }
  if (tab === 'card') {
    const hasBank = features.someActive('hasAddBank', 'hasConsumerAddBank');
    const payNowTitle = hasBank
      ? getContent('addBank.cardTabLabel')
      : getContent('bnpl.payNow.label');

    return { title: payNowTitle };
  }
  if (tab === 'bnpl') {
    const payLaterTitle = getContent('bnpl.payLater.label');

    return { title: payLaterTitle };
  }
  return { title: '' };
};

export {
  getBankState,
  getCardState,
  getCommonFormState,
  getDialingCode,
  getFieldCurrentState,
  getFieldData,
  getForcedValidCopy,
  getFormTabContent,
  getInstallmentTermState,
};
